import { useState } from "react";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHeader, DataTable, StatusBadge, Sel, Field, opts } from "@/components/kit";
import { api, useApi, fmtErr, dt, peso, label } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATS = ["regular", "vip", "tenant", "staff"];

function VehicleForm({ init, onClose, onSaved }) {
  const [f, setF] = useState(init);
  const save = async () => {
    try {
      await api.post("/vehicles", f);
      toast.success("Vehicle saved");
      onSaved();
      onClose();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="bg-card" data-testid="vehicle-dialog">
        <DialogHeader><DialogTitle>Vehicle</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Plate"><Input data-testid="vehicle-plate" className="font-mono uppercase" value={f.plate} onChange={(e) => setF({ ...f, plate: e.target.value.toUpperCase() })} /></Field>
          <Field label="Type"><Sel value={f.vehicle_type} onChange={(v) => setF({ ...f, vehicle_type: v })} options={opts(["car", "motorcycle", "van", "truck"])} testid="vehicle-type" className="w-full" /></Field>
          <Field label="Category"><Sel value={f.category} onChange={(v) => setF({ ...f, category: v })} options={opts(CATS)} testid="vehicle-category" className="w-full" /></Field>
          <Field label="RFID card"><Input data-testid="vehicle-rfid" value={f.rfid || ""} onChange={(e) => setF({ ...f, rfid: e.target.value.toUpperCase() })} /></Field>
          <Field label="Make / model"><Input data-testid="vehicle-make" value={f.make || ""} onChange={(e) => setF({ ...f, make: e.target.value })} /></Field>
          <Field label="Owner"><Input data-testid="vehicle-owner" value={f.owner || ""} onChange={(e) => setF({ ...f, owner: e.target.value })} /></Field>
        </div>
        <Button data-testid="vehicle-save" onClick={save}>Save</Button>
      </DialogContent>
    </Dialog>
  );
}

function History({ plate, onClose }) {
  const { data } = useApi(plate ? `/vehicles/${encodeURIComponent(plate)}/history` : null, {});
  return (
    <Dialog open={!!plate} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl bg-card" data-testid="vehicle-history-dialog">
        <DialogHeader><DialogTitle>History — <span className="plate">{plate}</span></DialogTitle></DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto">
          <DataTable testid="vehicle-history-table" rows={data?.sessions} columns={[
            { key: "session_no", label: "Session", render: (r) => <span className="font-mono text-xs">{r.session_no}</span> },
            { key: "branch_code", label: "Branch" },
            { key: "entry_time", label: "Entry", render: (r) => dt(r.entry_time) },
            { key: "exit_time", label: "Exit", render: (r) => dt(r.exit_time) },
            { key: "amt", label: "Amount", render: (r) => peso(r.final_amount ?? r.amount_due) },
            { key: "status", label: "Status", render: (r) => <StatusBadge status={r.status} /> },
          ]} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function Vehicles() {
  const { can } = useAuth();
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [edit, setEdit] = useState(null);
  const [hist, setHist] = useState(null);
  const { data, loading, reload } = useApi("/vehicles", { q, category: cat });
  return (
    <div className="fade-up">
      <PageHeader title="Vehicles" subtitle="Registered and seen vehicles with RFID, category and watchlist flags" testid="vehicles-header">
        <Input data-testid="vehicles-search" className="w-44" placeholder="Search plate" value={q} onChange={(e) => setQ(e.target.value)} />
        <Sel value={cat} onChange={setCat} testid="vehicles-category-filter" allLabel="All categories" options={opts(CATS)} />
        {can("edit") && <Button data-testid="vehicle-add-btn" onClick={() => setEdit({ plate: "", vehicle_type: "car", category: "regular" })}><Plus className="h-4 w-4" /> Vehicle</Button>}
      </PageHeader>
      <DataTable testid="vehicles-table" loading={loading} rows={data} onRowClick={(r) => setHist(r.plate)} columns={[
        { key: "plate", label: "Plate", render: (r) => <span className="plate text-xs">{r.plate}</span> },
        { key: "vehicle_type", label: "Type", render: (r) => label(r.vehicle_type) },
        { key: "category", label: "Category", render: (r) => <StatusBadge status={r.category} /> },
        { key: "watchlist", label: "Watchlist", render: (r) => (r.watchlist ? <StatusBadge status={r.watchlist} /> : "—") },
        { key: "rfid", label: "RFID" },
        { key: "make", label: "Make" },
        { key: "visits", label: "Visits" },
        { key: "last_seen", label: "Last seen", render: (r) => dt(r.last_seen) },
        { key: "act", label: "", render: (r) => can("edit") && <button className="text-xs text-primary underline" data-testid={`vehicle-edit-${r.plate}`} onClick={(e) => { e.stopPropagation(); setEdit(r); }}>Edit</button> },
      ]} />
      {edit && <VehicleForm init={edit} onClose={() => setEdit(null)} onSaved={reload} />}
      <History plate={hist} onClose={() => setHist(null)} />
    </div>
  );
}
